import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {$convertToMarkdownString} from '@lexical/markdown';
import {
  aiSearchConversationIdAtom,
  aiSearchIsStreamingAtom,
  aiSearchTurnsAtom,
} from 'atoms/aiSearch';
import {snackbarAtom} from 'atoms/snackbar';
import {AiQueryAssistantState, emptyAssistant, newTurnId} from 'components/ai/aiQueryConversation';
import {ToolApprovalPreview} from 'components/ai/ToolApprovalPreview';
import ellipsisStyles from 'components/DSYS/chat/AnimatedEllipsis.module.css';
import {CompanyOSGlyphLoading} from 'components/DSYS/CompanyOSLogo';
import {TRANSFORMERS} from 'components/DSYS/editor/transformers';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {StatusChip} from 'components/DSYS/StatusChip';
import {useAtomValue, useSetAtom} from 'jotai';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ApprovalMetadata,
  ApprovalPreviewField,
  streamApproveAction,
} from 'views/admin/adminAiQueryApi';

export type ToolApprovalProps = {
  approval: ApprovalMetadata;
  item: Record<string, unknown>;
  /**
   * The `elevated` of the turn that proposed it: the continuation runs with
   * that turn's access, so its downloads replay the same way.
   */
  elevated?: boolean;
};

export const ToolApprovalInline = ({approval, item, elevated}: ToolApprovalProps) => {
  const {t} = useTranslation();
  const conversationId = useAtomValue(aiSearchConversationIdAtom);
  const setTurns = useSetAtom(aiSearchTurnsAtom);
  const setIsStreaming = useSetAtom(aiSearchIsStreamingAtom);
  const setSnackbar = useSetAtom(snackbarAtom);
  // The token is spent once /approve is called, so a failed action can't be
  // approved again from the same card.
  const [status, setStatus] = useState<'idle' | 'pending' | 'done' | 'denied' | 'failed'>('idle');
  const [editedComment, setEditedComment] = useState<string | null>(null);
  const [editedBody, setEditedBody] = useState<string | null>(null);

  const isWorkstreamUpdate = approval.execute_tool === 'create_workstream_update';
  const comment = editedComment ?? (typeof item.comment === 'string' ? item.comment : '');
  const itemStatus = typeof item.status === 'string' ? item.status : null;
  const body = editedBody ?? approval.preview_body ?? '';

  const handleApprove = async () => {
    if (!conversationId || status !== 'idle') {
      return;
    }
    setStatus('pending');
    setIsStreaming(true);

    const startedAt = Date.now();
    const turnId = newTurnId();
    setTurns((prev) => [...prev, {id: turnId, question: '', assistant: emptyAssistant(true)}]);

    const executeArgs = {...approval.execute_args};
    if (isWorkstreamUpdate && editedComment !== null) {
      executeArgs.comment = editedComment;
    }
    if (approval.body_field && editedBody !== null) {
      executeArgs[approval.body_field] = editedBody;
    }

    const updateAssistant = (
      updater: (a: AiQueryAssistantState) => Partial<AiQueryAssistantState>
    ) =>
      setTurns((prev) =>
        prev.map((turn) =>
          turn.id === turnId
            ? {...turn, assistant: {...turn.assistant, ...updater(turn.assistant)}}
            : turn
        )
      );

    try {
      const final = await streamApproveAction(
        conversationId,
        approval.token,
        approval.execute_tool,
        executeArgs,
        {
          onTurn: (datasetTurnId) => updateAssistant(() => ({datasetTurnId, elevated})),
          onText: (delta) => updateAssistant((a) => ({answer: a.answer + delta})),
          onToolCall: (call) =>
            updateAssistant((a) => ({trace: [...a.trace, {kind: 'call', call}]})),
          onToolResult: (result) =>
            updateAssistant((a) => ({trace: [...a.trace, {kind: 'result', result}]})),
          onDataset: (dataset) =>
            updateAssistant((a) => ({
              datasets: {...a.datasets, [dataset.handle ?? dataset.capability]: dataset},
            })),
        }
      );
      updateAssistant(() => ({
        results: final.results,
        answer: final.answer_markdown || '',
        recipe: final.recipe ?? null,
        isStreaming: false,
        thoughtSeconds: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
      }));
      setStatus('done');
    } catch (e) {
      updateAssistant(() => ({
        error: e instanceof Error ? e.message : String(e),
        isStreaming: false,
      }));
      setSnackbar({text: t('failed_to_execute_action')});
      setStatus('failed');
    } finally {
      setIsStreaming(false);
    }
  };

  const handleDeny = () => {
    setStatus('denied');
  };

  if (status === 'denied') {
    return (
      <Box display="flex" style={{justifyContent: 'flex-end'}}>
        <Box
          padding="12"
          borderRadius="Large"
          className={atoms({backgroundColor: 'Secondary Surface'})}
          style={{maxWidth: '80%'}}
        >
          <Text size="small" color="subtle">
            {t('action_cancelled')}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Stack gap="8">
      <Box display="flex" style={{justifyContent: 'flex-end'}}>
        <Box
          padding="16"
          borderRadius="Large"
          className={atoms({backgroundColor: 'Secondary Surface'})}
          style={{maxWidth: '80%', width: '100%'}}
        >
          <Stack gap="12">
            <Box display="flex" alignItems="center" style={{gap: 8}}>
              <Text size="large" isBold>
                {approval.preview_title}
              </Text>
            </Box>

            {approval.preview_subtitle && (
              <Text size="small" color="subtle">
                {approval.preview_subtitle}
              </Text>
            )}

            {isWorkstreamUpdate && itemStatus && (
              <Box display="flex" alignItems="center" style={{gap: 8}}>
                <StatusChip status={itemStatus} size="small" />
              </Box>
            )}

            {isWorkstreamUpdate && (
              <Box
                borderRadius="Medium"
                className={atoms({
                  backgroundColor: status === 'idle' ? 'Background Base' : 'Secondary Surface',
                })}
              >
                <RichTextArea
                  editable={status === 'idle'}
                  value={comment}
                  disableTables
                  topOffset={0}
                  minHeight={60}
                  maxHeight={200}
                  placeholder={t('update_narrative_placeholder')}
                  onChange={(state) => {
                    state.read(() => {
                      setEditedComment($convertToMarkdownString(TRANSFORMERS));
                    });
                  }}
                />
              </Box>
            )}

            {!isWorkstreamUpdate && (
              <ToolApprovalPreview
                approval={approval}
                body={body}
                editable={status === 'idle'}
                onBodyChange={setEditedBody}
              />
            )}

            {(status === 'idle' || status === 'pending') && (
              <Box display="flex" justifyContent="flex-end" style={{gap: 8}}>
                <Button variant="opacity" onClick={handleDeny} disabled={status !== 'idle'}>
                  {t('deny')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void handleApprove()}
                  disabled={status !== 'idle' || !conversationId}
                  isLoading={status === 'pending'}
                >
                  {t('approve')}
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {status === 'idle' && (
        <Box display="flex" alignItems="center" style={{gap: 6}}>
          <CompanyOSGlyphLoading variant="pulse" style={{fontSize: 14}} />
          <Text size="small" color="subtle" className={ellipsisStyles.animated}>
            {t('awaiting_confirmation')}
          </Text>
        </Box>
      )}
    </Stack>
  );
};

const isPreviewField = (value: unknown): value is ApprovalPreviewField =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as Record<string, unknown>).label === 'string' &&
  typeof (value as Record<string, unknown>).value === 'string';

export const parseApprovalMetadata = (item: Record<string, unknown>): ApprovalMetadata | null => {
  const approval = item.approval;
  if (!approval || typeof approval !== 'object') {
    return null;
  }
  const a = approval as Record<string, unknown>;
  if (
    typeof a.token !== 'string' ||
    typeof a.execute_tool !== 'string' ||
    typeof a.preview_title !== 'string'
  ) {
    return null;
  }
  return {
    token: a.token,
    execute_tool: a.execute_tool,
    execute_args: (a.execute_args as Record<string, unknown>) ?? {},
    preview_title: a.preview_title,
    preview_subtitle: typeof a.preview_subtitle === 'string' ? a.preview_subtitle : null,
    preview_fields: Array.isArray(a.preview_fields)
      ? (a.preview_fields as unknown[]).filter(isPreviewField)
      : [],
    preview_body: typeof a.preview_body === 'string' ? a.preview_body : null,
    body_field: typeof a.body_field === 'string' ? a.body_field : null,
    warning: typeof a.warning === 'string' ? a.warning : null,
  };
};
